import Swiper from "swiper";
import { Navigation, Pagination, Manipulation } from "swiper/modules";

import { IContext } from "@src/context";
import { addFavoriteItem, removeFavoriteItem } from "@src/utils/favorite";
import { fetchFavoritedProperties, fetchFavoritedParkingSpots } from "./fetch";
import { renderSlides } from "./slides";
import {
  getPropertyIdsFromSlides,
  pushViewItemListEventFromSlides,
  pushSelectItemEventFromId,
} from "./analytic";

import "./index.css";

export function setupShortList(context: IContext) {
  const container = document.querySelector(".container-shortlist");

  if (container == null) {
    return;
  }

  // add slides
  Promise.all([
    fetchFavoritedProperties(context),
    fetchFavoritedParkingSpots(context),
  ]).then(([properties, parkingSpots]) => {
    const swiperOptions = {
      slidesPerView: 1,
      spaceBetween: 32,
      autoHeight: true,
      loop: properties.length >= 6, // loop only if there are more than slidesPerView * 2 slides
      breakpoints: {
        768: {
          slidesPerView: 2,
          spaceBetween: 32,
        },
        1104: {
          slidesPerView: 3,
          spaceBetween: 48,
        },
      },
      modules: [Navigation, Pagination, Manipulation],
      navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev",
      },
      pagination: {
        el: ".swiper-pagination",
        clickable: true,
      },
    };

    // init swiper
    const propertySwiper = new Swiper(
      ".container-shortlist-swiper[data-type='apartment']",
      swiperOptions
    );
    const parkingSpotSwiper = new Swiper(
      ".container-shortlist-swiper[data-type='parking']",
      swiperOptions
    );

    // clear slides
    propertySwiper.removeAllSlides();
    parkingSpotSwiper.removeAllSlides();

    const propertySlides = renderSlides(context, properties);
    const parkingSpotSlides = renderSlides(context, parkingSpots);

    const propertyEmpty = container.querySelector(
      ".container-shortlist-empty[data-type='apartment']"
    );
    const parkingSpotEmpty = container.querySelector(
      ".container-shortlist-empty[data-type='parking']"
    );

    if (propertySlides.length > 0) {
      propertySlides.forEach((slide) => {
        propertySwiper.appendSlide(slide);
      });
      propertyEmpty?.removeAttribute("data-empty");
    } else {
      propertyEmpty?.setAttribute("data-empty", "true");
    }

    if (parkingSpotSlides.length > 0) {
      parkingSpotSlides.forEach((slide) => {
        parkingSpotSwiper.appendSlide(slide);
      });
      parkingSpotEmpty?.removeAttribute("data-empty");
    } else {
      parkingSpotEmpty?.setAttribute("data-empty", "true");
    }

    // GTM push `view_item_list` event when first load list
    pushViewItemListEventFromSlides(
      getVisibleSlides(propertySwiper.activeIndex),
      "apartment"
    );
    pushViewItemListEventFromSlides(
      getVisibleSlides(parkingSpotSwiper.activeIndex),
      "parking"
    );

    // init previous slides
    let perviousPropertyIds = getPropertyIdsFromSlides(
      getVisibleSlides(propertySwiper.activeIndex)
    );
    let perviousParkingSpotIds = getPropertyIdsFromSlides(
      getVisibleSlides(parkingSpotSwiper.activeIndex)
    );

    // slide change
    propertySwiper.on("slideChangeTransitionEnd", function () {
      const currentSlides = getVisibleSlides(propertySwiper.activeIndex);
      // GTM push `view_item_list` event when slide change
      pushViewItemListEventFromSlides(
        // exclude previous slides
        currentSlides.filter(
          (slide: HTMLElement) =>
            !perviousPropertyIds.includes(getPropertyIdsFromSlides([slide])[0])
        ),
        "apartment"
      );
      // update previous slides
      perviousPropertyIds = getPropertyIdsFromSlides(currentSlides);
    });

    parkingSpotSwiper.on("slideChangeTransitionEnd", function () {
      const currentSlides = getVisibleSlides(parkingSpotSwiper.activeIndex);
      // GTM push `view_item_list` event when slide change
      pushViewItemListEventFromSlides(
        // exclude previous slides
        currentSlides.filter(
          (slide: HTMLElement) =>
            !perviousParkingSpotIds.includes(
              getPropertyIdsFromSlides([slide])[0]
            )
        ),
        "parking"
      );
      // update previous slides
      perviousParkingSpotIds = getPropertyIdsFromSlides(currentSlides);
    });

    function getVisibleSlides(activeIndex: number) {
      return propertySwiper.slides.slice(
        activeIndex,
        activeIndex +
          (typeof propertySwiper.params.slidesPerView === "number"
            ? propertySwiper.params.slidesPerView
            : 0)
      );
    }
  });

  // GTM push `select_item` event
  container.addEventListener("click", function (e) {
    const source = e.target as HTMLElement;
    if (
      (source && source.classList.contains("container-shortlist-item-link")) ||
      source.closest(".container-shortlist-item-image a")
    ) {
      const propertyId = source
        .closest(".container-shortlist-item")
        ?.querySelector(".container-shortlist-favorite")
        ?.getAttribute("data-property-id");
      const type = source
        ?.closest(".container-shortlist-swiper")
        ?.getAttribute("data-type");
      pushSelectItemEventFromId(propertyId || "", type || "");
    }
  });

  // toggle favorite
  container.addEventListener("click", (event) => {
    if (event.target instanceof HTMLElement) {
      const btn = event.target.closest(".container-shortlist-favorite");
      const propertyId = btn?.getAttribute("data-property-id");
      const type = btn
        ?.closest(".container-shortlist-swiper")
        ?.getAttribute("data-type");
      if (btn && propertyId) {
        if (btn.getAttribute("data-favorite") === "true") {
          // remove from favorite
          btn.setAttribute("data-favorite", "false");
          removeFavoriteItem(propertyId, type || undefined);
        } else {
          // add to favorite
          btn.setAttribute("data-favorite", "true");
          addFavoriteItem(propertyId, type || undefined);
        }
      }
    }
  });
}
